import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  createAdmin: (data) => api.post('/auth/create-admin', data),
  deductCredits: (amount, reason) => api.post('/auth/deduct-credits', { amount, reason }),
  getCreditConfig: () => api.get('/auth/credit-config'),
};

// Admin APIs
export const adminAPI = {
  // Users
  listUsers: (params) => api.get('/admin/users', { params }),
  updateUserStatus: (userId, isActive) => api.put(`/admin/users/${userId}/status`, null, { params: { is_active: isActive } }),
  
  // Clients
  createClient: (data) => api.post('/admin/clients', data),
  listClients: (params) => api.get('/admin/clients', { params }),
  getClient: (id) => api.get(`/admin/clients/${id}`),
  updateClient: (id, data) => api.put(`/admin/clients/${id}`, data),
  
  // Leads
  listLeads: (params) => api.get('/admin/leads', { params }),
  updateLeadStatus: (id, status) => api.put(`/admin/leads/${id}/status`, null, { params: { status } }),
  assignLead: (id, assignedTo) => api.put(`/admin/leads/${id}/assign`, null, { params: { assigned_to: assignedTo } }),
  convertLead: (id) => api.post(`/admin/leads/${id}/convert`),
  
  // Projects
  createProject: (data) => api.post('/admin/projects', data),
  listProjects: (params) => api.get('/admin/projects', { params }),
  updateProject: (id, data) => api.put(`/admin/projects/${id}`, data),
  
  // Invoices
  createInvoice: (data) => api.post('/admin/invoices', data),
  listInvoices: (params) => api.get('/admin/invoices', { params }),
  
  // Content
  createPage: (data) => api.post('/admin/content/pages', data),
  listPages: () => api.get('/admin/content/pages'),
  updatePage: (id, data) => api.put(`/admin/content/pages/${id}`, data),
  
  // Blog
  createPost: (data) => api.post('/admin/blog', data),
  listPosts: () => api.get('/admin/blog'),
  
  // Analytics
  getAnalytics: () => api.get('/admin/analytics/overview'),
};

// Client Portal APIs
export const clientAPI = {
  getProfile: () => api.get('/client/profile'),
  getProjects: () => api.get('/client/projects'),
  getProject: (id) => api.get(`/client/projects/${id}`),
  getTickets: (params) => api.get('/client/tickets', { params }),
  createTicket: (data) => api.post('/client/tickets', data),
  getInvoices: () => api.get('/client/invoices'),
  getInvoice: (id) => api.get(`/client/invoices/${id}`),
  initiatePayment: (id) => api.post(`/client/invoices/${id}/pay`),
  verifyPayment: (id, data) => api.post(`/client/invoices/${id}/verify-payment`, null, { params: data }),
};

// AI APIs
export const aiAPI = {
  evaluate: (data) => api.post('/ai/evaluate', data),
  buildScope: (data) => api.post('/ai/scope', data),
  generateProposal: (data) => api.post('/ai/proposal', data),
  generatePrototype: (data) => api.post('/ai/prototype', data),
  submitLead: (data) => api.post('/ai/submit-lead', data),
  downloadProposalPDF: (leadId) => api.get(`/ai/leads/${leadId}/proposal-pdf`, { responseType: 'blob' }),
  getLead: (leadId) => api.get(`/ai/leads/${leadId}`),
  chat: (sessionId, message, history) => api.post('/ai/chat', { session_id: sessionId, message, history }),
};

// Prototype APIs (async generation + polling pattern)
export const prototypeAPI = {
  generateTeaser: (leadId) => api.post('/prototypes/teaser', { lead_id: leadId }),
  generateFull: (leadId) => api.post('/prototypes/full', { lead_id: leadId }),
  get: (prototypeId) => api.get(`/prototypes/${prototypeId}`),
  listForLead: (leadId) => api.get(`/prototypes/lead/${leadId}`),
  listMine: () => api.get('/prototypes/user/me'),
};

// Build (Engineering) APIs — AI-generated full-stack codebases
export const buildAPI = {
  generate: (leadId) => api.post('/builds/generate', { lead_id: leadId }),
  get: (buildId) => api.get(`/builds/${buildId}`),
  listForLead: (leadId) => api.get(`/builds/lead/${leadId}`),
  getFile: (buildId, path) => api.get(`/builds/${buildId}/file`, { params: { path } }),
  download: (buildId) => api.get(`/builds/${buildId}/download`, { responseType: 'blob' }),
  runTests: (buildId) => api.post(`/builds/${buildId}/test`),
  packageRelease: (buildId) => api.post(`/builds/${buildId}/release`),
  downloadRelease: (buildId) => api.get(`/builds/${buildId}/release-download`, { responseType: 'blob' }),
};

// Workspace APIs
export const workspaceAPI = {
  getProjects: () => api.get('/workspace/projects'),
  claimLead: (leadId) => api.post(`/workspace/claim/${leadId}`),
  chat: (leadId, message) => api.post('/workspace/chat', { lead_id: leadId, message }),
  getMessages: (leadId) => api.get(`/workspace/projects/${leadId}/messages`),
  postEvent: (leadId, content) => api.post(`/workspace/projects/${leadId}/events`, { content }),
  generateProposal: (leadId) => api.post(`/workspace/projects/${leadId}/generate-proposal`),
};

// Payments APIs (Razorpay credit packs)
export const paymentsAPI = {
  getPacks: () => api.get('/payments/credit-packs'),
  createOrder: (packId) => api.post('/payments/create-credit-order', { pack_id: packId }),
  verify: (data) => api.post('/payments/verify-credit-purchase', data),
  getTransactions: () => api.get('/payments/transactions'),
};

// Public APIs
export const publicAPI = {
  getPage: (slug) => api.get(`/public/pages/${slug}`),
  listPages: (pageType) => api.get('/public/pages', { params: { page_type: pageType } }),
  listBlogPosts: (params) => api.get('/public/blog', { params }),
  getBlogPost: (slug) => api.get(`/public/blog/${slug}`),
  getTechStack: () => api.get('/public/tech-stack'),
  getPricing: () => api.get('/public/pricing'),
  getGlobalPresence: () => api.get('/public/global-presence'),
};

// Upwork Scout APIs
export const upworkAPI = {
  getJobs: (params) => api.get('/upwork/jobs', { params }),
  getJob: (jobId) => api.get(`/upwork/jobs/${jobId}`),
  scout: (queries) => api.post('/upwork/scout', { queries }),
  importJob: (job) => api.post('/upwork/import-job', job),
  importBulk: (data) => api.post('/upwork/import-bulk', data),
  importEmail: (htmlContent, source = 'email_manual') => api.post('/upwork/import-email', { html_content: htmlContent, source }),
  importEmailBatch: (emails, source = 'email_batch') => api.post('/upwork/import-email-batch', { emails, source }),
  scanEmails: () => api.post('/upwork/scan-emails'),
  getScanLogs: () => api.get('/upwork/scan-logs'),
  getEmailConfig: () => api.get('/upwork/email-config'),
  generateProposal: (jobId) => api.post(`/upwork/generate-proposal/${jobId}`),
  generateAllProposals: () => api.post('/upwork/generate-all-proposals'),
  updateStatus: (jobId, status) => api.patch(`/upwork/jobs/${jobId}/status`, { status }),
  deleteJob: (jobId) => api.delete(`/upwork/jobs/${jobId}`),
  getStats: () => api.get('/upwork/stats'),
};

// Normalize FastAPI error responses into a safe display string.
// 422 validation errors return detail as an ARRAY of objects ({type, loc, msg, input, url})
// which must never be rendered directly in React.
export const apiErrorMessage = (error, fallback = 'Something went wrong') => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const field = Array.isArray(d?.loc) ? d.loc[d.loc.length - 1] : '';
        return field ? `${field}: ${d?.msg || 'invalid'}` : (d?.msg || 'Invalid input');
      })
      .join(' · ') || fallback;
  }
  if (detail && typeof detail === 'object') return detail.msg || fallback;
  return fallback;
};

export default api;
